import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, Calendar, Users, User,
  Building2, CreditCard, CalendarDays, Mail, Paintbrush, Settings,
  HelpCircle, ChevronsUpDown, Terminal, Percent, TrendingUp, Store,
  Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { bookingAPI } from '@/services/api';

const navSections = [
  {
    title: 'Main',
    items: [
      { to: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/app/appointments', icon: CalendarDays, label: 'Appointments' },
      { to: '/app/clients', icon: Users, label: 'Clients' },
      { to: '/app/team', icon: Users, label: 'Team' },
    ]
  },
  {
    title: 'Manage',
    items: [
      { to: '/app/calendars', icon: Calendar, label: 'Calendars' },
      { to: '/app/organization', icon: Building2, label: 'Organization' },
      { to: '/app/billing', icon: CreditCard, label: 'Billing' },
      { to: '/app/reviews', icon: Star, label: 'Reviews' },
    ]
  },
  {
    title: 'Scale',
    items: [
      { to: '/app/forecasting', icon: TrendingUp, label: 'AI Forecasting' },
      { to: '/app/pos', icon: Terminal, label: 'POS Hardware' },
      { to: '/app/tax', icon: Percent, label: 'Tax & VAT' },
      { to: '/app/reseller', icon: Store, label: 'Reseller Program' },
    ]
  },
  {
    title: 'System',
    items: [
      { to: '/app/settings', icon: Settings, label: 'Settings' },
      { to: '/app/profile', icon: User, label: 'Profile' },
      { to: '/app/email-templates', icon: Mail, label: 'Email Templates' },
      { to: '/app/personalization', icon: Paintbrush, label: 'Personalization' },
      { to: '/app/guide', icon: HelpCircle, label: 'Guide' },
    ]
  }
];

export default function Sidebar({ open, onToggle }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [upcomingCount, setUpcomingCount] = useState(0);

  useEffect(() => {
    bookingAPI.getStats().then(res => {
      setUpcomingCount(res.data?.upcoming || 0);
    }).catch(() => {});
  }, []);

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onToggle?.();
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[45] lg:hidden"
          style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
          onClick={onToggle}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 h-screen z-50 flex flex-col transition-transform duration-300 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        style={{
          width: '260px',
          background: 'var(--dash-grad-sidebar)',
          borderRight: '1px solid var(--dash-border)',
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center gap-2.5 px-5 h-16 shrink-0"
          style={{ borderBottom: '1px solid var(--dash-border)' }}
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--dash-grad-btn)' }}
          >
            <CalendarDays className="w-[15px] h-[15px]" style={{ color: 'var(--dash-bg)' }} />
          </div>
          <span className="font-bold text-[15px] tracking-tight" style={{ color: 'var(--dash-text)' }}>
            ScheduleX
          </span>
        </div>

        {/* Nav sections */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {navSections.map((section) => (
            <div key={section.title}>
              <div
                className="text-[10px] font-semibold uppercase tracking-[0.12em] px-4 pt-5 pb-2"
                style={{ color: 'var(--dash-text4)' }}
              >
                {section.title}
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname.startsWith(item.to);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={handleNavClick}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 rounded-[10px] text-[13.5px] font-medium transition-all relative",
                        isActive
                          ? "text-[var(--dash-text)]"
                          : "text-[var(--dash-text3)] hover:text-[var(--dash-text2)]"
                      )}
                      style={{
                        background: isActive ? 'var(--dash-nav-active)' : 'transparent',
                      }}
                    >
                      {isActive && (
                        <div
                          className="absolute left-[-16px] top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-[3px]"
                          style={{ background: 'var(--dash-accent)' }}
                        />
                      )}
                      <item.icon className="w-[18px] h-[18px] shrink-0" />
                      <span>{item.label}</span>
                      {item.to === '/app/appointments' && upcomingCount > 0 && (
                        <span
                          className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-md"
                          style={{ background: 'var(--dash-surface2)', color: 'var(--dash-text2)' }}
                        >
                          {upcomingCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User */}
        <div
          className="px-4 py-4 shrink-0"
          style={{ borderTop: '1px solid var(--dash-border)' }}
        >
          <button
            onClick={() => { navigate('/app/profile'); handleNavClick(); }}
            className="flex items-center gap-3 p-2 rounded-xl w-full text-left transition-colors cursor-pointer"
            style={{ transition: 'background .2s' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--dash-nav-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
              style={{ background: 'var(--dash-surface3)', color: 'var(--dash-text)' }}
            >
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: 'var(--dash-text)' }}>
                {user?.name || 'User'}
              </div>
              <div className="text-[11px] truncate" style={{ color: 'var(--dash-text4)' }}>
                {user?.email || ''}
              </div>
            </div>
            <ChevronsUpDown className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--dash-text4)' }} />
          </button>
        </div>
      </aside>
    </>
  );
}
